/**
 * Feature: Roofing Invoicing & Payments
 * /features/invoicing
 */
import {
  FeatureFaq,
  FeatureStats,
  FeatureSignup,
  FeatureHeroBadge,
  FeatureChecklist,
} from '@/components/marketing/FeaturePageSections';

import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Link } from 'react-router-dom';
import { getAuthLink } from '@/lib/links';
import { softwareApplicationSchema, faqPageSchema } from '@/lib/schemas';
import { Helmet } from 'react-helmet-async';
import { Brain, CreditCard, Bell, BarChart3, FileText, Building2, ArrowRight } from 'lucide-react';
import { ProductShowcase } from '@/components/marketing/ProductShowcase';
import { PRODUCT_FRAMES } from '@/lib/productFrames';
import { CapabilityLedger } from '@/components/marketing/CapabilityLedger';

const faqs = [
  {
    question: 'What payment methods do customers use?',
    answer:
      'Credit/debit cards (Visa, Mastercard, Amex), ACH bank transfer, and third-party financing options. All processed through Stripe with enterprise-grade security.',
  },
  {
    question: 'Can I send invoices from the job site?',
    answer:
      'Yes. Rooflyzer is fully mobile — generate and send an invoice from your phone the moment you complete a job walkthrough.',
  },
  {
    question: 'Does Rooflyzer handle deposits?',
    answer:
      'Yes. Set deposit percentages or fixed amounts, collect at proposal signing, then collect the balance on completion. All tracked automatically.',
  },
  {
    question: 'Can I brand my invoices?',
    answer:
      'Fully branded — your logo, colors, and contact info on every invoice. Customers see your brand, not ours.',
  },
  {
    question: 'Does Rooflyzer integrate with QuickBooks?',
    answer: 'Yes. Sync invoices and payments to QuickBooks automatically. No double entry.',
  },
];

const invoicingStats = [
  {
    value: 'Minutes',
    label: 'To create an invoice',
    desc: 'From the estimate, one click',
  },
  {
    // Not "card, ACH, or financing": the customer invoice link is
    // read-only today (apps/web-app/src/pages/public/InvoicePaymentPage.tsx).
    value: 'One link',
    label: 'Branded customer invoice',
    desc: 'Line items, balance, and how to pay',
  },
  {
    value: 'Automated',
    label: 'Payment reminders',
    desc: '3, 7, and 14 days overdue',
  },
  { value: '$0 extra', label: 'Invoice link cost', desc: 'Included in every plan' },
];

export const InvoicingPage = () => {
  const appSchema = softwareApplicationSchema({
    name: 'Rooflyzer OS — Roofing Invoicing & Payments',
    description:
      'Create professional roofing invoices, collect payments online, and track outstanding balances. Integrated with your CRM and job management pipeline.',
    featureList: [
      'One-click invoice from estimate',
      'Online payment portal',
      'Automated payment reminders',
      'Accounts receivable dashboard',
      'Partial payments & deposits',
      'Multi-job client billing',
    ],
    url: 'https://rooflyzer.pro/features/invoicing',
    offers: { price: '0', description: 'Free tier available — no credit card required' },
  });

  const faqSchema = faqPageSchema(faqs);

  return (
    <>
      <SEOHead
        title="Roofing Invoicing & Payment Collection Software"
        description="Create professional roofing invoices, collect payments online, and track outstanding balances. Integrated with your CRM and job management pipeline."
        canonicalUrl="/features/invoicing"
        ogImage="/og/invoicing.png"
        keywords="roofing invoicing software, roofing payment collection, roofing billing software, contractor invoicing"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="pt-24 min-h-screen px-4 sm:px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <Breadcrumbs
            items={[
              { name: 'Features', url: '/features' },
              { name: 'Invoicing & Payments', url: '/features/invoicing' },
            ]}
            className="mb-8"
          />

          {/* Hero */}
          <section className="text-center max-w-4xl mx-auto mb-20">
            <FeatureHeroBadge icon={CreditCard}>Get Paid Faster</FeatureHeroBadge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              Roofing Invoices That Go From Job Close to{' '}
              <span className="text-[#C29BF8]">Cash in Hours</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Generate branded invoices the moment a job is complete. Accept payments via credit
              card, ACH, or financing. Track every dollar from proposal to final payment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-[#7950A2] text-white hover:bg-[#68418f] font-semibold px-8 shadow-md shadow-[#7950A2]/20"
                onClick={() => window.location.assign(getAuthLink('/register'))}
              >
                Send Your First Invoice Free
                <ArrowRight className="ml-2 size-5" />
              </Button>
              <Link to="/pricing">
                <Button
                  size="lg"
                  variant="ghost"
                  className="border border-white/20 text-white hover:bg-white/10"
                >
                  See Payment Features
                </Button>
              </Link>
            </div>
          </section>

          {/* Product film */}
          <section className="mb-24">
            <ProductShowcase frame={PRODUCT_FRAMES.invoices} float={false} />
          </section>

          {/* Capability Grid */}
          <section className="mb-24">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">
              Everything You Need to Get Paid
            </h2>
            <CapabilityLedger
              items={[
                {
                  icon: Brain,
                  title: 'One-Click Invoice from Estimate',
                  desc: 'Convert approved estimates to invoices instantly. All line items, materials, and labor transfer automatically.',
                },
                {
                  icon: CreditCard,
                  title: 'Online Payment Portal',
                  desc: 'Customers pay via a secure branded link. Accept Visa, Mastercard, ACH bank transfer, and financing.',
                },
                {
                  icon: Bell,
                  title: 'Automated Payment Reminders',
                  desc: 'Set follow-up sequences that send reminders at 3, 7, and 14 days overdue. Reduce manual chasing.',
                },
                {
                  icon: BarChart3,
                  title: 'Accounts Receivable Dashboard',
                  desc: 'See every outstanding invoice, total owed, and aging buckets at a glance across all your jobs.',
                },
                {
                  icon: FileText,
                  title: 'Partial Payments & Deposits',
                  desc: 'Collect deposits at signing and final payment at completion. Track balances and payment history automatically.',
                },
                {
                  icon: Building2,
                  title: 'Multi-Job Client Billing',
                  desc: 'Bill a single customer across multiple properties or jobs with consolidated statements.',
                },
              ]}
            />
          </section>

          {/* Problem → Solution */}
          <section className="mb-24 bg-white/5 backdrop-blur-xl rounded-3xl p-8 sm:p-12 border border-white/10">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">The Old Way</h2>
                <ul className="space-y-4">
                  {[
                    'Print or PDF invoices emailed manually',
                    'Customers mail checks — waits days or weeks',
                    'Chase overdue invoices by phone call',
                    'Reconcile payments in spreadsheets',
                    "No visibility into what's owed until month end",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/80">
                      <span className="text-red-400 mt-0.5 text-lg">✕</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">The Rooflyzer Way</h2>
                <FeatureChecklist
                  items={[
                    'Digital invoices delivered via secure link instantly',
                    'Customers pay online in minutes from any device',
                    'Automated reminders chase overdue invoices for you',
                    'Real-time payment tracking in your dashboard',
                    'Live accounts receivable across all active jobs',
                  ]}
                />
              </div>
            </div>
          </section>

          {/* Stats */}
          <FeatureStats title="What getting paid looks like" items={invoicingStats} />

          {/* FAQ */}
          <FeatureFaq faqs={faqs} />

          {/* Bottom CTA */}
          <FeatureSignup
            title="Ready to Get Paid Faster?"
            buttonLabel="Send Your First Invoice Free"
          >
            Join roofing contractors who switched from paper invoices to branded, trackable ones.
          </FeatureSignup>
        </div>
      </div>
    </>
  );
};

export default InvoicingPage;
